/* System defined core library */
import { Injectable } from '@angular/core';
import { Observable, Observer } from 'rxjs';
import * as moment from 'moment';

/* User defined library/components */
import { UtilityService } from '../../../../shared/services/utility.service';
import { OrchestrationApiService } from '../orchestration-api.service';
import { PaymentApiService } from '../payment-api.service';
import { SelectedExamDetailService } from '../selected-exam-detail.service';

/* User defined models */
import { AppointmentDetail } from '../../../../shared/models/scheduling/appointmentdetail.model';
import { AppointmentSlot } from '../../../../shared/models/scheduling/appointmentSlot.model';
import { Booking } from '../../../../shared/models/scheduling/booking.model';
import { Candidate } from '../../../../shared/models/scheduling/candidate.model';
import { CardDetail } from '../../../../shared/models/scheduling/carddetail.model';
import { Exam } from '../../../../shared/models/scheduling/exam.model';
import { ExamSet } from '../../../../shared/models/scheduling/examSet.model';
import { Hold } from '../../../../shared/models/scheduling/hold.model';
import { Payment } from '../../../../shared/models/scheduling/payment.model';
import { Session } from '../../../../shared/models/scheduling/session.model';
import { Test } from '../../../../shared/models/scheduling/test.model';

/* User defined enums/constants */
import { configSettings, PaymentCodeMaps, TargetFlow, voucherPaymentCodes } from '../../../../shared/constants/scheduling.constant';
import { ExamStatus } from '../../../../shared/enums/scheduling.enum';


@Injectable()
export class AppointmentService {

  // extended time properties for calculating TestingAccommodation Duration
  readonly ExtendedTimeAccommodationName: string = 'EXTENDED TIME';
  readonly ExtendedTimeAdjustDurationAttributeName: string = 'Adjust Duration';
  readonly ExtendedTimeAdjustDurationOperandParameterName: string = 'Operand';
  readonly ExtendedTimeAdjustDurationOperandParameterValue: string = 'Add';
  readonly ExtendedTimeAdjustDurationValueParameterName: string = 'Value';

  constructor(
    private orchestrationApiService: OrchestrationApiService,
    private paymentApiService: PaymentApiService,
    private selectedExamDetailService: SelectedExamDetailService,
    private utilityService: UtilityService
  ) {
  }

  /* Given an appointment, returns duration, optionally from testing accommodations */
  public getAppointmentDuration(appointment: AppointmentDetail, includeAccommodations: boolean = false): number {

    // if appointment has no status, or is cancelled, return no appointment duration
    if (!appointment || !appointment.status || appointment.status.toLowerCase() === 'cancelled') {
      return 0;
    }

    let duration = appointment.appointmentSlot.duration;

    // look for additional duration from testing accommodations
    if (!(includeAccommodations && appointment && appointment.exam && appointment.exam.testingAccommodations)) {
      return duration;
    }

    // check testing accomodations for 'EXTENDED TIME'
    let extendedTimeAccommodation = appointment.exam.testingAccommodations.find(x => x.name === this.ExtendedTimeAccommodationName);
    if (extendedTimeAccommodation) {

      // look up attribute named 'Adjust Duration' for extension times
      let adjustDurationAttribute = extendedTimeAccommodation.attributes.find(x => x.name === this.ExtendedTimeAdjustDurationAttributeName);

      // if found, look up .parameters for 'Operand' of value 'Add' and 'Value' of X for minutes
      if (adjustDurationAttribute) {
        let operandParameter = adjustDurationAttribute.parameters.find(x => x.name === this.ExtendedTimeAdjustDurationOperandParameterName && x.value === this.ExtendedTimeAdjustDurationOperandParameterValue);
        let valueParameter = adjustDurationAttribute.parameters.find(x => x.name === this.ExtendedTimeAdjustDurationValueParameterName);

        // add duration only if add operand found w/ value parameter
        if (operandParameter && valueParameter) {
          let operandAddValue = parseInt(valueParameter.value) || 0;
          duration += operandAddValue;
        }
      }
    }

    return duration;
  }

  /** 
   * Helper method to set payment details on appointments
   * @param paymentDetails
   * @param appointmentDetail
   */
  public setPaymentDetailsOnAppointment(
    paymentDetails: Payment[],
    appointmentDetail: AppointmentDetail
  ) {
    paymentDetails.forEach(payment => {
      if (voucherPaymentCodes.indexOf(payment.paymentType.toLowerCase()) !== -1) {
        this.selectedExamDetailService.setVoucherPayment([payment]);
        this.selectedExamDetailService.voucherTransactionId = payment.transactionId;
      }
      if (payment.examName.trim() == appointmentDetail.exam.name.trim()) {
        appointmentDetail.payments.push(payment);
      }
    });
    return appointmentDetail;
  }

  /**
   * Helper method to check if the total duration should be displayed
   * @param appointmentData
   */
  public canShowDuration(appointmentData: AppointmentDetail[]): boolean {
    //multiday exams should not display the total duration
    if (!!appointmentData[0] && appointmentData[0].exam.sessions) {
      return !(appointmentData[0].exam.sessions.length > 1);
    }
    return true;
  }

  public canShowReceiptBtn(selectedAppts: AppointmentDetail[]): boolean {
    return selectedAppts.some(appt => appt.payments.some(payment => payment.amount > 0));
  }

  /**
   * Helper method to set selected and disabled status in a multiday appointment based on which checkbox was toggled
   * @param appointmentData
   * @param event
   * @param sessionIndex
   */
  public selectSession(
    appointmentData: AppointmentDetail[],
    event: any,
    sessionIndex: number
  ): AppointmentDetail[] {
    appointmentData[0].exam.sessions.forEach((session: Session, index: number) => {
      session.selected = false;
      session.disabled = false;
      if (event.target.checked) {
        if (index > sessionIndex) {
          session.selected = true;
          session.disabled = true;
        }
      }
    });
    appointmentData[0].exam.sessions[sessionIndex].selected = event.target.checked;
    return appointmentData;
  }

  public validateVoucherPayment(appointmentDetails: AppointmentDetail[], candidate: Candidate, overlapStartDate: any, overlapEndDate: any) {
    return new Observable((observer: Observer<any>) => {
      let voucherPayment = appointmentDetails[0].payments.filter(p => voucherPaymentCodes.indexOf(p.paymentType.toLowerCase()) !== -1);
      this.selectedExamDetailService.setVoucherPayment(voucherPayment);
      let voucherDetail: CardDetail = new CardDetail();
      voucherDetail.currency = voucherPayment[0].currency;
      let voucherRequestModel = {};
      let examDetails: any[] = [];

      this.selectedExamDetailService.voucherTransactionId = voucherPayment[0].transactionId;

      appointmentDetails.forEach((appointment: AppointmentDetail) => {
        examDetails.push({
          "examId": appointment.exam.id,
          "amount": voucherPayment[0].amount
        });
      });

      // picking the site of first exam
      // TODO: handle this for multiple exams?
      let selectedSite = appointmentDetails[0].appointmentSlot.site;

      voucherRequestModel['card'] = voucherDetail;
      voucherRequestModel['sessions'] = examDetails;
      // just picking first exam for now
      voucherRequestModel['examDuration'] = appointmentDetails[0].appointmentSlot.duration;
      voucherRequestModel['applicationCode'] = 'pro';
      voucherRequestModel['siteId'] = selectedSite.id;
      voucherRequestModel['siteCountry'] = selectedSite.location.address.country;
      voucherRequestModel['testDate'] = moment(appointmentDetails[0].appointmentSlot.startDateTime).format('YYYY-MM-DDTHH:mm:ss');
      voucherRequestModel['confirmationNumber'] = appointmentDetails[0].ConfirmationNumber;
      voucherRequestModel['candidateId'] = candidate.Id;

      let transactionId = voucherPayment[0].transactionId;

      this.paymentApiService.validateVoucherDetails(voucherRequestModel, transactionId).subscribe({
        next: (response) => {
          if (response.isValid) {
            this.selectedExamDetailService.setVoucherExpDate(response.expirationDate);

            // set overlap from and to date based on voucher
            if (response.expirationDate) {
              let overlapDateRange = this.utilityService.getVoucherStartEndDates(response.expirationDate, overlapStartDate, overlapEndDate);
              if (!!overlapDateRange) {
                this.selectedExamDetailService.setOverlapsFromDate(overlapDateRange[0]);
                this.selectedExamDetailService.setOverlapsToDate(overlapDateRange[1]);
              }
            }
            observer.next(true);
            observer.complete();
          } else {
            observer.next(false);
            observer.complete();
          }
        },
        error: (err) => {
          observer.next(false);
          observer.complete();
        }
      });
    });
  }

  /**
   * Helper method to select all exams with the Select All checkbox
   * @param appointmentData
   * @param event
   */
  selectAllExamsInBooking(event: any, appointmentData: AppointmentDetail[]): any {
    let emitObject: any = {
      'emitIsAppointmentSelected': false,
      'appointmentData': []
    }
    var notAllCancelled = false;
    for (var i = 0; i < appointmentData.length; i++) {
      if (appointmentData[i].status != 'cancelled') {
        notAllCancelled = true;
      }
    }
    emitObject.appointmentData = appointmentData;
    if (!event) {
      for (var i = 0; i < appointmentData.length; i++) {
        emitObject.appointmentData[i].selected = false;
      }
      emitObject.emitIsAppointmentSelected = true;
    }
    else {
      emitObject.emitIsAppointmentSelected = false;

      if (notAllCancelled) {
        emitObject.emitIsAppointmentSelected = true;
      }
    }
    return emitObject;
  }

  /**
   * Helper method to check Reschedule policy on various configurations
   * @param appointmentData
   * @param requiredReschedule
   * @param consecutiveDays
   * @param showReschedule = true
   */
  public checkForReschedulePolicyError(appointmentData: AppointmentDetail[],
    requiredReschedule: boolean, consecutiveDays: boolean, showReschedule: boolean = true): any {
    if (!appointmentData.length || (appointmentData.length && !showReschedule)) {
      return {
        'error': false
      }
    }
    if (consecutiveDays) {
      //multiday consecutive
      const anySessionsFalse = (session: Session) => !session.canReschedulePolicy;
      return {
        "error": appointmentData[0].exam.sessions.some(anySessionsFalse),
        "message": "appointmentLookupFormComponent.cannotBeRescheduledError"
      };
    }
    else if (appointmentData[0].exam.sessions && appointmentData[0].exam.sessions.length > 1) {
      //multiday non-consecutive
      if (appointmentData[0].exam.sessions.some(s => s.selected && !s.canReschedulePolicy)) {
        return {
          "error": true,
          "message": "appointmentLookupFormComponent.oneOrMoreCannotBeRescheduledError"
        };
      }
      return {
        'error': false
      }
    }
    else if (!appointmentData[0].exam.sessions && !appointmentData[0].canReschedulePolicy && appointmentData.length === 1) {
      //single appointment
      return {
        "error": true,
        "message": "appointmentLookupFormComponent.cannotBeRescheduledError"
      };
    }

    if (requiredReschedule) {
      //reschedule required b2b
      const anyFalse = (appointmentDetail: AppointmentDetail) => !appointmentDetail.canReschedulePolicy;
      return {
        "error": appointmentData.some(anyFalse),
        "message": "appointmentLookupFormComponent.cannotBeRescheduledError"
      };
    }
    else if (appointmentData.length > 1) {
      //not required b2b
      if (appointmentData.some(appt => !appt.canReschedulePolicy)) {
        return {
          "error": true,
          "message": "appointmentLookupFormComponent.oneOrMoreCannotBeRescheduledError"
        };
      }
      return {
        'error': false
      }
    }

    return {
      'error': false
    }
  }

  public checkForAllExamsCanceled(
    bookingDetails: Booking
  ): boolean {
    // Native API implementation, Status is coming in capital.
    return !bookingDetails.tests.filter((tempTest: Test) => tempTest.status.toLowerCase() != 'cancelled').length;
  }

  /**
   * Helper method to add Reschedule policy to an appointmentdetail
   * @param bookingDetails
   * @param eligibilityErrorOccured
   * @param examData
   */
  public checkForEligibilityError(
    bookingDetails: Booking,
    eligibilityErrorOccured: boolean,
    examData: any[]
  ): boolean {
    // TODO: Need to change API changes has been done for Cancellation Eligibility.
    if (examData != null && examData != undefined && examData[0].code != "EligibilityError") {
      eligibilityErrorOccured = false;
    }
    // not all exams are eligibility based. In that case we need to make sure display the error correctly
    else if (
      bookingDetails.tests[0].eligibility &&
      bookingDetails.tests[0].eligibility.id !== '' &&
      bookingDetails.tests[0].eligibility.id !== null
    ) {
      eligibilityErrorOccured = true;
    } else if (
      bookingDetails.tests[0].eligibility &&
      (bookingDetails.tests[0].eligibility.id == '' || bookingDetails.tests[0].eligibility.id == null)
    ) {
      eligibilityErrorOccured = false;
    }

    return eligibilityErrorOccured;
  }

  /**
   * sets right apptSlot on the apptDetail for a session selected
   * @param appointmentDetail
   * @param sessionIndex
   */
  public getApptDetail(appointmentDetail: AppointmentDetail, sessionIndex: number) {
    if (appointmentDetail.exam.sessions && appointmentDetail.exam.sessions.length < 1) return appointmentDetail;
    const session = appointmentDetail.exam.sessions[sessionIndex];
    let appointmentSlot =
      new AppointmentSlot(null, null, new Date(this.utilityService.getFormatToFullMMDDYYYY(session.slot.startDateTime, "/")), session.duration, session.site, session.slot.testTime, session.slot.testEndTime);
    appointmentDetail.appointmentSlot = appointmentSlot;
    return appointmentDetail;
  }

  /** Returns true/false based on the user targetFlow
   * and b2b attributes to hide checkboxes on the appointments
   * */
  public hideCheckBoxes() {
    if (this.selectedExamDetailService.currentTargetFlow === TargetFlow.AppointmentLookup)
      return this.selectedExamDetailService.isBackToBackRequired &&
        (this.selectedExamDetailService.rescheduleSetAttributeCode || this.selectedExamDetailService.cancelSetAttributeCode);
    if (this.selectedExamDetailService.currentTargetFlow === TargetFlow.Reschedule)
      return this.selectedExamDetailService.isBackToBackRequired && this.selectedExamDetailService.rescheduleSetAttributeCode;
    if (this.selectedExamDetailService.currentTargetFlow === TargetFlow.Cancel)
      return this.selectedExamDetailService.isBackToBackRequired && this.selectedExamDetailService.cancelSetAttributeCode;
    return false;
  }

  public getAppointmentDetails(bookingDetails: Booking): AppointmentDetail {
    let test = bookingDetails.tests[0];
    let tempAppointmentDetail = new AppointmentDetail();
    tempAppointmentDetail = this.setTempApptDetail(test, bookingDetails);

    tempAppointmentDetail.exam.sessions = [];
    bookingDetails.tests.forEach((test: Test, index: number) => {
      tempAppointmentDetail.exam.sessions.push(test.sessions[0]);
      tempAppointmentDetail.exam.sessions[index].hold = new Hold(test.sessions[0].hold.id, test.sessions[0].hold.href);
      tempAppointmentDetail.exam.sessions[index].confirmationNumber = test.sessions[0].confirmationNumber;
      tempAppointmentDetail.exam.sessions[index].appointmentId = test.sessions[0].id;
      tempAppointmentDetail.exam.sessions[index].canReschedulePolicy = test.sessions[0].canReschedule;
    });
    return tempAppointmentDetail;
  }

  public setTempApptDetail(test: any, bookingDetails: any) {
    let tempAppointmentDetail = new AppointmentDetail(
      test.program,
      test.exam,
      bookingDetails.candidate,
      test.sessions[0].confirmationNumber,
      test.status.toLowerCase(),
      test.sessions[0].hold.id,
      test.sessions[0].id,
      [],
    );
    tempAppointmentDetail.canReschedulePolicy = test.sessions[0].canReschedule;
    this.selectedExamDetailService.setProgram(test.program);
    this.selectedExamDetailService.programCode = test.program.code;
    return tempAppointmentDetail;
  }

  /***
   * checks for config from DB to prevent reschedule, cancel. introduced for ASE Battery(does not allow resched in IWR)
   * @param bookingDetails
   **/
  public checkProgramForConfig(bookingDetails: Booking): void {
    this.orchestrationApiService.getConfiguration(
        [configSettings.SkipReschedule, configSettings.CancelNotAllowed, configSettings.SkipMetrics],
      bookingDetails.client.code,
      bookingDetails.tests[0].program.code
    ).subscribe((resp: any) => {
      this.selectedExamDetailService.skipMetrics = resp[configSettings.SkipMetrics] === 'True';
      this.selectedExamDetailService.setSkipReschedule(resp[configSettings.SkipReschedule] === 'True');
      this.selectedExamDetailService.setCancelNotAllowed(resp[configSettings.CancelNotAllowed] === 'True');
    });
  }

  /**
   * checks if a given exam can schedule in the current proctor/nonProctor modality
   * !!exam: sometimes a component will send an undefined exam
   * this.selectedExamDetailService.isProctored(true):
   *  * exam.proctorScheduling: if the exam is schedulable in Proctor sites
   *  * !exam.sessions: multiday check for Reschedule, in Reschedule flow only multiday appts have the sessions array
   *  * exam.sessions.length === 1: multiday check for Schedule, in Schedule flow all exams have the session array, but only multiday has multiple sessions
   * this.selectedExamDetailService.isProctored(false):
   *  * exam.nonProctorScheduling: if the exam is schedulable in B&M sites
   * @param exam
   */
  public canScheduleExamInFlow(exam: Exam): boolean {
    return !!exam && (this.selectedExamDetailService.isProctored ? (exam.proctorScheduling && (!exam.sessions || exam.sessions.length === 1)) : exam.nonProctorScheduling) && exam.status !== ExamStatus.InvalidSchedulingMethod.toString();
  }

  public canScheduleAppointmentsInFlow(selectedAppointments: AppointmentDetail[]): boolean {
    return !!selectedAppointments && !!selectedAppointments.length && selectedAppointments.every(a => this.canScheduleExamInFlow(a.exam));
  }

  public canScheduleSetInFlow(set: ExamSet): boolean {
    return (
      !!set &&
      !!set.attributes &&
      !!set.exams &&
      !!set.exams.length &&
      (!!set.attributes.requiredSchedule ?
        set.exams.every(e => this.canScheduleExamInFlow(e)) :
        set.exams.some(e => this.canScheduleExamInFlow(e))
      )
    );
  }

  public canRescheduleSetInFlow(appts: AppointmentDetail[]): boolean {
    return (
      !!appts &&
      !!appts.length &&
      (this.selectedExamDetailService.rescheduleSetAttributeCode ?
        appts.every(a => this.canScheduleExamInFlow(a.exam)) :
        appts.some(a => this.canScheduleExamInFlow(a.exam))
      )
    );
  }
}
